import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Search, Download, Plus, ArrowLeft, List, Grid, Users, CheckCircle } from 'lucide-react';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import RowActions from '../../components/RowActions';
import ConfirmDialog from '../../components/ConfirmDialog';
import EditableCell from '../../components/EditableCell';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, coalesce } from '../../utils/helpers';
import { EXTINGUISHER_TYPES, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';

const ExtinguisherInventory = () => {
  const [view, setView] = useState('list');
  const [viewMode, setViewMode] = useState('table');
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [inventory, setInventory] = useState([]);
  const [allInventory, setAllInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filterLocation, setFilterLocation] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState({ open: false, item: null });
  const [editingCellKey, setEditingCellKey] = useState(null);
  const [savingCellKey, setSavingCellKey] = useState(null);
  const [addingRow, setAddingRow] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    siteId: '',
    serialNumber: '',
    inventoryNumber: '',
    extinguisherType: '',
    detailedType: '',
    capacity: '',
    detailedLocation: '',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    spareParts: '',
    observation: '',
  });
  
  const { success: showSuccess, error: showError } = useToast();

  useEffect(() => {
    loadClients();
    loadAllInventory();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadClientInventory(selectedClient.id);
      loadSites(selectedClient.id);
    }
  }, [selectedClient]);

  useEffect(() => {
    applyFilters();
  }, [inventory, searchQuery, filterLocation]);

  useEffect(() => {
    filterClients();
  }, [searchQuery, clients, view]);

  useEffect(() => {
    if (formData.clientId) loadSites(formData.clientId);
  }, [formData.clientId]);

  const loadClients = async () => {
    try {
      const data = await apiService.getClients();
      setClients(data);
      setFilteredClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllInventory = async () => {
    try {
      const data = await apiService.getExtinguisherInventory(null, null, null);
      setAllInventory(data);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    }
  };

  const loadClientInventory = async (clientId) => {
    try {
      setLoading(true);
      const data = await apiService.getExtinguisherInventory(clientId, null, null);
      setInventory(data);
    } catch (error) {
      showError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async (clientId) => {
    try {
      const data = await apiService.getSitesByClient(clientId);
      setSites(data);
      return data;
    } catch (error) {
      console.error('Failed to load sites:', error);
      return [];
    }
  };

  const filterClients = () => {
    if (view !== 'list' || !searchQuery) {
      setFilteredClients(clients);
      return;
    }
    const query = searchQuery.toLowerCase();
    setFilteredClients(clients.filter(c => c.name?.toLowerCase().includes(query) || c.company?.toLowerCase().includes(query)));
  };

  const getClientSummary = (clientId) => {
    const clientInventory = allInventory.filter(e => e.clientId === clientId);
    const today = new Date();
    const ok = clientInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; return n && (n - today) / 86400000 > 30; }).length;
    const urgent = clientInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; if (!n) return false; const d = (n - today) / 86400000; return d > 0 && d <= 10; }).length;
    const expired = clientInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; return n && n < today; }).length;
    return { total: clientInventory.length, ok, urgent, expired, hasData: clientInventory.length > 0 };
  };

  const totalClients = clients.length;
  const clientsWithData = clients.filter(c => getClientSummary(c.id).hasData).length;
  const totalExtinguishers = allInventory.length;
  const totalOk = allInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; return n && (n - new Date()) / 86400000 > 30; }).length;

  const applyFilters = () => {
    let filtered = inventory;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((i) =>
        [i.inventoryNumber, i.serialNumber, i.detailedLocation, i.location, i.detailedType, i.extinguisherType, i.observation]
          .some((v) => v?.toLowerCase().includes(query))
      );
    }
    if (filterLocation) {
      filtered = filtered.filter((i) =>
        (i.detailedLocation || i.location || '').toLowerCase().includes(filterLocation.toLowerCase())
      );
    }
    setFilteredInventory(filtered);
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setView('detail');
    setSearchQuery('');
    setFilterLocation('');
    setSites([]);
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedClient(null);
    setInventory([]);
    setFilteredInventory([]);
    setSearchQuery('');
    setSites([]);
    loadAllInventory();
  };

  const handleResetClient = async (client) => {
    if (!window.confirm(`⚠️ Supprimer TOUS les extincteurs pour "${client.name}"?`)) return;
    try {
      const clientInventory = allInventory.filter(e => e.clientId === client.id);
      for (const item of clientInventory) await apiService.deleteEquipment(item.id);
      showSuccess(`Extincteurs supprimés pour ${client.name}`);
      loadAllInventory();
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const buildFormFromItem = (item = {}) => ({
    clientId: item.clientId || selectedClient?.id || '',
    siteId: item.siteId || '',
    serialNumber: item.serialNumber || '',
    inventoryNumber: item.inventoryNumber || '',
    extinguisherType: item.extinguisherType || '',
    detailedType: item.detailedType || '',
    capacity: item.capacity || '',
    detailedLocation: item.detailedLocation || item.location || '',
    lastMaintenanceDate: item.lastMaintenanceDate ? item.lastMaintenanceDate.split('T')[0] : '',
    nextMaintenanceDate: item.nextMaintenanceDate ? item.nextMaintenanceDate.split('T')[0] : '',
    spareParts: item.spareParts || '',
    observation: item.observation || '',
  });

  const openAddModal = async () => {
    setEditingItem(null);
    let clientSites = sites;
    if (selectedClient && !clientSites.length) {
      clientSites = await loadSites(selectedClient.id);
    }
    const base = buildFormFromItem();
    setFormData({ ...base, siteId: base.siteId || clientSites[0]?.id || '' });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData(buildFormFromItem(item));
    setShowModal(true);
  };

  const openViewModal = (item) => setViewItem(item);

  const handleDeleteItem = async () => {
    if (!deleteItemConfirm.item) return;
    try {
      await apiService.deleteEquipment(deleteItemConfirm.item.id);
      showSuccess('Extincteur supprimé');
      setDeleteItemConfirm({ open: false, item: null });
      if (selectedClient) loadClientInventory(selectedClient.id);
      loadAllInventory();
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'lastMaintenanceDate' && value) {
      const d = new Date(value);
      d.setMonth(d.getMonth() + 6);
      setFormData({ ...formData, [name]: value, nextMaintenanceDate: d.toISOString().split('T')[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const buildPayloadFromItem = (item, overrides = {}) => {
    const merged = { ...buildFormFromItem(item), ...overrides };
    if (overrides.lastMaintenanceDate) {
      const d = new Date(overrides.lastMaintenanceDate);
      d.setMonth(d.getMonth() + 6);
      merged.nextMaintenanceDate = d.toISOString().split('T')[0];
    }
    return { type: 'extinguisher', ...merged };
  };

  const handleInlineUpdate = async (item, field, newValue) => {
    const cellKey = `${item.id}-${field}`;
    setSavingCellKey(cellKey);
    try {
      const full = await apiService.getEquipmentById(item.id);
      const payload = buildPayloadFromItem(full, { [field]: newValue });
      const result = await apiService.updateEquipment(item.id, payload);
      if (result.success !== false) {
        setInventory((prev) =>
          prev.map((row) =>
            row.id === item.id
              ? {
                  ...row,
                  ...full,
                  [field]: newValue,
                  ...(field === 'lastMaintenanceDate'
                    ? { nextMaintenanceDate: payload.nextMaintenanceDate }
                    : {}),
                }
              : row
          )
        );
        loadAllInventory();
      } else {
        showError(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      showError(error.message?.includes('Serial number') ? `N° série "${newValue}" existe déjà` : error.message || 'Erreur');
    } finally {
      setSavingCellKey(null);
    }
  };

  const suggestSerialNumber = () => {
    const prefix = (selectedClient?.name || 'EXT').substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, '') || 'EXT';
    const existing = new Set(inventory.map((i) => i.serialNumber));
    let num = inventory.length + 1;
    let serial = `${prefix}-${String(num).padStart(3, '0')}`;
    while (existing.has(serial)) {
      num += 1;
      serial = `${prefix}-${String(num).padStart(3, '0')}`;
    }
    return serial;
  };

  const handleAddRow = async () => {
    if (!selectedClient) return;
    let clientSites = sites;
    if (!clientSites.length) {
      clientSites = await loadSites(selectedClient.id);
    }
    if (!clientSites.length) {
      showError('Aucun site disponible. Créez d\'abord un site pour ce client.');
      return;
    }
    setAddingRow(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const next = new Date();
      next.setMonth(next.getMonth() + 6);
      const payload = {
        type: 'extinguisher',
        clientId: selectedClient.id,
        siteId: clientSites[0].id,
        serialNumber: suggestSerialNumber(),
        capacity: '6kg',
        extinguisherType: Object.values(EXTINGUISHER_TYPES)[0] || 'CO2',
        detailedLocation: '',
        detailedType: 'Extincteur',
        lastMaintenanceDate: today,
        nextMaintenanceDate: next.toISOString().split('T')[0],
      };
      const result = await apiService.createEquipment(payload);
      if (result.success !== false) {
        showSuccess('Nouvelle ligne ajoutée — double-cliquez une cellule pour modifier');
        await loadClientInventory(selectedClient.id);
        loadAllInventory();
      } else {
        showError(result.message || 'Erreur');
      }
    } catch (error) {
      showError(error.message || 'Erreur lors de l\'ajout');
    } finally {
      setAddingRow(false);
    }
  };

  const formatDateInput = (date) => (date ? date.split('T')[0] : '');

  const getTypeDisplay = (item) => coalesce(item.detailedType, item.extinguisherType, EQUIPMENT_TYPE_LABELS[item.type], item.type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { type: 'extinguisher', ...formData };
      const result = editingItem
        ? await apiService.updateEquipment(editingItem.id, payload)
        : await apiService.createEquipment(payload);
      if (result.success !== false) {
        showSuccess(editingItem ? 'Extincteur mis à jour' : 'Extincteur ajouté');
        setShowModal(false);
        setEditingItem(null);
        if (selectedClient) loadClientInventory(selectedClient.id);
        loadAllInventory();
      } else {
        showError(result.message || 'Erreur');
      }
    } catch (error) {
      showError(error.message?.includes('Serial number') ? `N° série "${formData.serialNumber}" existe déjà` : error.message);
    }
  };

  const columns = [
    {
      header: 'Client',
      accessor: 'name',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg"><ClipboardList className="w-5 h-5 text-red-600" /></div>
          <div><p className="font-semibold text-gray-900">{renderCell(row.name)}</p><p className="text-sm text-gray-500">{renderCell(row.company)}</p></div>
        </div>
      ),
    },
    { header: 'Total', accessor: 'total', render: (row) => { const s = getClientSummary(row.id); return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.total > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>{s.total}</span>; } },
    { header: 'OK', accessor: 'ok', render: (row) => { const s = getClientSummary(row.id); return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.ok > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>{s.ok}</span>; } },
    { header: 'Urgent', accessor: 'urgent', render: (row) => { const s = getClientSummary(row.id); return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.urgent > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>{s.urgent}</span>; } },
    { header: 'Expirés', accessor: 'expired', render: (row) => { const s = getClientSummary(row.id); return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.expired > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'}`}>{s.expired}</span>; } },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (row) => {
        const s = getClientSummary(row.id);
        return (
          <RowActions
            onView={() => handleViewClient(row)}
            onEdit={() => handleViewClient(row)}
            onDelete={() => handleResetClient(row)}
            disableDelete={!s.hasData}
          />
        );
      },
    },
  ];

  const locations = [...new Set(inventory.map(e => e.detailedLocation || e.location).filter(Boolean))];

  if (loading && view === 'list' && clients.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div></div>;
  }

  if (view === 'list') {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3"><ClipboardList className="w-8 h-8 text-red-600" />État des Extincteurs</h1>
            <p className="text-gray-600 mt-1">Inventaire détaillé des extincteurs par client</p>
          </div>
          <div className="flex gap-2">
            <Button variant={viewMode === 'table' ? 'primary' : 'outline'} size="sm" icon={List} onClick={() => setViewMode('table')}>Tableau</Button>
            <Button variant={viewMode === 'card' ? 'primary' : 'outline'} size="sm" icon={Grid} onClick={() => setViewMode('card')}>Cartes</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg"><Users className="w-5 h-5 text-white" /></div></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Total Clients</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalClients}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg"><ClipboardList className="w-5 h-5 text-white" /></div><span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">{totalClients > 0 ? Math.round((clientsWithData / totalClients) * 100) : 0}%</span></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Avec données</p>
            <p className="text-3xl font-extrabold text-gray-900">{clientsWithData}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg"><ClipboardList className="w-5 h-5 text-white" /></div></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Total Extincteurs</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalExtinguishers}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg"><CheckCircle className="w-5 h-5 text-white" /></div></div>
            <p className="text-sm font-bold text-gray-500 uppercase">OK</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalOk}</p>
          </motion.div>
        </div>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="flex-1"><Input placeholder="Rechercher un client..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} icon={Search} /></div>
            <Button variant="outline">Filtres</Button>
          </div>
        </Card>

        {viewMode === 'table' && (
          <Card>
            <div className="mb-4"><h2 className="text-lg font-semibold text-gray-900">{filteredClients.length} client(s)</h2></div>
            <Table columns={columns} data={filteredClients} />
          </Card>
        )}

        {viewMode === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client, index) => {
              const s = getClientSummary(client.id);
              return (
                <motion.div key={client.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                  <Card className="hover:shadow-lg transition-shadow">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-3 rounded-lg bg-red-100"><ClipboardList className="w-5 h-5 text-red-600" /></div>
                          <div><h3 className="text-lg font-bold text-gray-900">{client.name}</h3><p className="text-sm text-gray-500">{client.company}</p></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-blue-50 rounded-lg p-2"><p className="text-xl font-bold text-blue-600">{s.total}</p><p className="text-xs text-gray-500">Total</p></div>
                        <div className="bg-green-50 rounded-lg p-2"><p className="text-xl font-bold text-green-600">{s.ok}</p><p className="text-xs text-gray-500">OK</p></div>
                        <div className="bg-amber-50 rounded-lg p-2"><p className="text-xl font-bold text-amber-600">{s.urgent}</p><p className="text-xs text-gray-500">Urgent</p></div>
                        <div className="bg-red-50 rounded-lg p-2"><p className="text-xl font-bold text-red-600">{s.expired}</p><p className="text-xs text-gray-500">Expirés</p></div>
                      </div>
                      <div className="flex items-center justify-end pt-2 border-t border-gray-200">
                        <RowActions
                          onView={() => handleViewClient(client)}
                          onEdit={() => handleViewClient(client)}
                          onDelete={() => handleResetClient(client)}
                          disableDelete={!s.hasData}
                        />
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Detail View
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="outline" icon={ArrowLeft} onClick={handleBackToList} className="mb-4">Retour</Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3"><ClipboardList className="w-8 h-8 text-red-600" />{selectedClient?.name}</h1>
          <p className="text-gray-600 mt-1">{selectedClient?.company} - Inventaire extincteurs</p>
        </div>
        <div className="flex gap-3">
          <Button variant="primary" icon={Plus} onClick={handleAddRow} loading={addingRow}>Ajouter une ligne</Button>
          <Button variant="outline" icon={Plus} onClick={openAddModal}>Formulaire complet</Button>
          <Button variant="secondary" icon={Download}>Exporter</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><div className="text-center"><p className="text-3xl font-bold text-blue-600">{filteredInventory.length}</p><p className="text-sm text-gray-600">Total</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-green-600">{filteredInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; return n && (n - new Date()) / 86400000 > 30; }).length}</p><p className="text-sm text-gray-600">OK</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-amber-600">{filteredInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; if (!n) return false; const d = (n - new Date()) / 86400000; return d > 0 && d <= 10; }).length}</p><p className="text-sm text-gray-600">Urgent</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-red-600">{filteredInventory.filter(i => { const n = i.nextMaintenanceDate ? new Date(i.nextMaintenanceDate) : null; return n && n < new Date(); }).length}</p><p className="text-sm text-gray-600">Expirés</p></div></Card>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} icon={Search} />
          <Select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} placeholder="Tous les emplacements" options={locations.map(l => ({ value: l, label: l }))} />
        </div>
      </Card>

      <Card>
        {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div></div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">N°</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">N°/Inventaire</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">N°/Série</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Emplacement</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Contrôle</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Prochaine</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Observation</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase sticky right-0 z-20 bg-gray-50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInventory.length === 0 ? (
                  <tr><td colSpan="9" className="px-6 py-12 text-center"><ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="font-medium text-gray-900">Aucun extincteur</p><div className="flex justify-center gap-2 mt-3"><Button variant="primary" icon={Plus} onClick={handleAddRow} loading={addingRow}>Ajouter une ligne</Button><Button variant="outline" icon={Plus} onClick={openAddModal}>Formulaire complet</Button></div></td></tr>
                ) : filteredInventory.map((item, idx) => (
                  <motion.tr key={item.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.02 }} className="group hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm text-center">{idx + 1}</td>
                    <EditableCell
                      cellKey={`${item.id}-inventoryNumber`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={item.inventoryNumber}
                      onSave={(val) => handleInlineUpdate(item, 'inventoryNumber', val)}
                      saving={savingCellKey === `${item.id}-inventoryNumber`}
                      placeholder="Ex: INV-001"
                      className="px-3 py-3 text-sm text-center font-medium"
                    />
                    <EditableCell
                      cellKey={`${item.id}-serialNumber`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={item.serialNumber}
                      onSave={(val) => handleInlineUpdate(item, 'serialNumber', val)}
                      saving={savingCellKey === `${item.id}-serialNumber`}
                      placeholder="Ex: EXT-2024-001"
                      className="px-3 py-3 text-sm font-mono"
                    />
                    <EditableCell
                      cellKey={`${item.id}-detailedLocation`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={coalesce(item.detailedLocation, item.location)}
                      onSave={(val) => handleInlineUpdate(item, 'detailedLocation', val)}
                      saving={savingCellKey === `${item.id}-detailedLocation`}
                      placeholder="Ex: Hall principal, niveau 2"
                      className="px-3 py-3 text-sm"
                    />
                    <EditableCell
                      cellKey={`${item.id}-detailedType`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={coalesce(item.detailedType, item.extinguisherType) ?? ''}
                      onSave={(val) => handleInlineUpdate(item, 'detailedType', val)}
                      saving={savingCellKey === `${item.id}-detailedType`}
                      placeholder="Ex: CO2 2 kg"
                      className="px-3 py-3 text-sm"
                    />
                    <EditableCell
                      cellKey={`${item.id}-lastMaintenanceDate`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={formatDateInput(item.lastMaintenanceDate)}
                      onSave={(val) => handleInlineUpdate(item, 'lastMaintenanceDate', val)}
                      saving={savingCellKey === `${item.id}-lastMaintenanceDate`}
                      type="date"
                      className="px-3 py-3 text-sm text-center"
                    />
                    <EditableCell
                      cellKey={`${item.id}-nextMaintenanceDate`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={formatDateInput(item.nextMaintenanceDate)}
                      onSave={(val) => handleInlineUpdate(item, 'nextMaintenanceDate', val)}
                      saving={savingCellKey === `${item.id}-nextMaintenanceDate`}
                      type="date"
                      className="px-3 py-3 text-sm text-center"
                    />
                    <EditableCell
                      cellKey={`${item.id}-observation`}
                      editingKey={editingCellKey}
                      onStartEdit={setEditingCellKey}
                      onEndEdit={() => setEditingCellKey(null)}
                      value={item.observation}
                      onSave={(val) => handleInlineUpdate(item, 'observation', val)}
                      saving={savingCellKey === `${item.id}-observation`}
                      placeholder="Notes, anomalies..."
                      className="px-3 py-3 text-sm"
                    />
                    <td className="px-3 py-3 sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]">
                      <RowActions
                        onView={() => openViewModal(item)}
                        onEdit={() => openEditModal(item)}
                        onDelete={() => setDeleteItemConfirm({ open: true, item })}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            {filteredInventory.length > 0 && (
              <div className="flex justify-center border-t border-gray-100 pt-4 mt-2">
                <Button variant="outline" icon={Plus} onClick={handleAddRow} loading={addingRow} size="sm">
                  Ajouter une ligne
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditingItem(null); }} title={editingItem ? 'Modifier Extincteur' : 'Ajouter Extincteur'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-600">Client:</p><p className="font-semibold">{selectedClient?.name} - {selectedClient?.company}</p></div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Site" name="siteId" value={formData.siteId} onChange={handleFormChange} options={sites.map(s => ({ value: s.id, label: s.name }))} required />
            <Input label="N° Série" name="serialNumber" value={formData.serialNumber} onChange={handleFormChange} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="N° Inventaire" name="inventoryNumber" value={formData.inventoryNumber} onChange={handleFormChange} />
            <Select label="Type extincteur" name="extinguisherType" value={formData.extinguisherType} onChange={handleFormChange} options={Object.values(EXTINGUISHER_TYPES).map(t => ({ value: t, label: t }))} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Type détaillé" name="detailedType" value={formData.detailedType} onChange={handleFormChange} />
            <Input label="Capacité" name="capacity" value={formData.capacity} onChange={handleFormChange} required />
          </div>
          <Input label="Emplacement détaillé" name="detailedLocation" value={formData.detailedLocation} onChange={handleFormChange} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Date contrôle" type="date" name="lastMaintenanceDate" value={formData.lastMaintenanceDate} onChange={handleFormChange} required />
            <Input label="Prochaine" type="date" name="nextMaintenanceDate" value={formData.nextMaintenanceDate} onChange={handleFormChange} disabled />
          </div>
          <Textarea label="Pièces détachées" name="spareParts" value={formData.spareParts} onChange={handleFormChange} rows={2} />
          <Textarea label="Observation" name="observation" value={formData.observation} onChange={handleFormChange} rows={2} />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button type="submit" variant="primary">Enregistrer</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="Détails extincteur">
        {viewItem && (
          <div className="space-y-3 text-sm">
            <p><span className="font-semibold">N° inventaire:</span> {renderCell(viewItem.inventoryNumber)}</p>
            <p><span className="font-semibold">N° série:</span> {renderCell(viewItem.serialNumber)}</p>
            <p><span className="font-semibold">Emplacement:</span> {renderCell(coalesce(viewItem.detailedLocation, viewItem.location))}</p>
            <p><span className="font-semibold">Type:</span> {renderCell(coalesce(viewItem.detailedType, viewItem.extinguisherType))}</p>
            <p><span className="font-semibold">Observation:</span> {renderCell(viewItem.observation)}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setViewItem(null)}>Fermer</Button>
              <Button variant="primary" onClick={() => { openEditModal(viewItem); setViewItem(null); }}>Modifier</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteItemConfirm.open}
        onClose={() => setDeleteItemConfirm({ open: false, item: null })}
        onConfirm={handleDeleteItem}
        title="Supprimer l'extincteur"
        message={`Supprimer l'extincteur ${deleteItemConfirm.item?.serialNumber} ?`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  );
};

export default ExtinguisherInventory;
